import { CommonModule } from '@angular/common';
import { Component, OnInit, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { LearnerShareService } from './learner-share.service';

@Component({
  selector: 'app-learner-share-overlay',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './learner-share-overlay.component.html',
  styleUrl: './learner-share-overlay.component.scss'
})
export class LearnerShareOverlayComponent implements OnInit {
  readonly share = inject(LearnerShareService);

  expiresInDays = 7;
  includeEvidence = false;
  accessPin = '';
  personalNote = '';
  copied = false;
  showPin = false;
  shareWithAccount = false;
  shareTargetAccountId: string | null = null;

  readonly quickCohorts = [
    'Proud to achieve this milestone on SahkaarX!',
    'Excited to share progress on my skill journey!',
    'Huge step forward in my learning path!'
  ];

  async ngOnInit(): Promise<void> {
    this.shareTargetAccountId = await this.share.resolveShareTargetAccountId();
  }

  get canShareWithAccount(): boolean {
    return !!this.shareTargetAccountId;
  }

  get canGoBack(): boolean {
    return this.share.optionsContext() !== null && this.share.panel() !== 'options';
  }

  goBack(): void {
    if (this.canGoBack) {
      this.share.panel.set('options');
    }
  }

  selectExpiry(days: number): void {
    this.expiresInDays = days;
  }

  togglePinVisibility(): void {
    this.showPin = !this.showPin;
  }

  applyQuickCohort(text: string): void {
    this.share.communityShareText.set(text);
  }

  getOptionIconType(id: string): string {
    const lower = (id || '').toLowerCase();
    if (lower.includes('snapshot')) return 'snapshot';
    if (lower.includes('live') || lower.includes('profile')) return 'live';
    if (lower.includes('community') || lower.includes('sahkaar') || lower.includes('cohort')) return 'community';
    if (lower.includes('360')) return 'learner360';
    return 'default';
  }

  get panelTitle(): string {
    switch (this.share.panel()) {
      case 'options':
        return this.share.optionsContext()?.title || 'Share Options';
      case 'platform':
        return 'Share your achievement';
      case 'learner360':
        return 'Learner 360° Portfolio';
      case 'community':
        return 'Post to SahkaarX';
      default:
        return 'Share';
    }
  }

  private get activeAchievement() {
    return this.share.achievementContext();
  }

  get achievementTitle(): string {
    return (
      this.activeAchievement?.title ||
      this.share.shareResult()?.title ||
      'Learning Achievement'
    );
  }

  get achievementDescription(): string {
    const desc = this.activeAchievement?.description || this.share.shareResult()?.description;
    if (desc) return desc;
    return 'I have achieved a milestone on SahkaarX.';
  }

  get careerFitScoreMetric(): string | null {
    const desc = this.achievementDescription;
    const arrowMatch = desc.match(/(\d+(?:\.\d+)?)\s*(?:→|->)\s*(\d+(?:\.\d+)?)/);
    if (arrowMatch) {
      return `${arrowMatch[1]} → ${arrowMatch[2]}`;
    }
    const scoreMatch = desc.match(/score\s+(?:improved\s+to|is)\s+(\d+(?:\.\d+)?)/i);
    if (scoreMatch) {
      return scoreMatch[1];
    }
    if (this.activeAchievement?.metadata?.['fitScore']) {
      return this.activeAchievement.metadata['fitScore'];
    }
    return null;
  }

  get weeksMetric(): string | null {
    const title = this.achievementTitle;
    const weekMatch = title.match(/(\d+)[-\s]week/i) || title.match(/week\s+(\d+)/i);
    if (weekMatch) {
      return weekMatch[1];
    }
    if (this.activeAchievement?.metadata?.['weeks']) {
      return this.activeAchievement.metadata['weeks'];
    }
    return null;
  }

  get skillsGainedMetric(): string | null {
    if (this.activeAchievement?.metadata?.['skillsGainedCount']) {
      return this.activeAchievement.metadata['skillsGainedCount'];
    }
    const desc = this.achievementDescription;
    const skillMatch = desc.match(/(\d+)\s+skills/i);
    if (skillMatch) {
      return skillMatch[1];
    }
    return null;
  }

  loadingOptionId: string | null = null;

  async onOption(id: string): Promise<void> {
    const ctx = this.share.optionsContext();
    if (!ctx || this.loadingOptionId || this.share.busy()) return;

    this.loadingOptionId = id;
    const opt = ctx.options.find((o) => o.id === id);
    if (opt) opt.loading = true;

    try {
      await ctx.onSelect(id);
    } finally {
      this.loadingOptionId = null;
      if (opt) opt.loading = false;
    }
  }

  async create360(): Promise<void> {
    if (!this.shareTargetAccountId) {
      this.shareTargetAccountId = await this.share.resolveShareTargetAccountId();
    }
    await this.share.createLearner360({
      expiresInDays: this.expiresInDays,
      includeEvidence: this.includeEvidence,
      accessPin: this.accessPin,
      targetAccountId:
        this.shareWithAccount && this.shareTargetAccountId
          ? this.shareTargetAccountId
          : undefined
    });
  }

  async onCopyLink(): Promise<void> {
    await this.share.copyShareUrl();
    this.copied = true;
    setTimeout(() => {
      this.copied = false;
    }, 2500);
  }

  async onNativeShare(): Promise<void> {
    if (this.share.canNativeShare()) {
      await this.share.nativeShare();
    } else {
      await this.onCopyLink();
    }
  }

  async onShareAchievement(): Promise<void> {
    if (this.personalNote.trim() && this.activeAchievement) {
      this.share.communityShareText.set(this.personalNote.trim());
      await this.share.openSahkaarXForAchievement(this.activeAchievement.id, this.activeAchievement.code);
      return;
    }

    if (this.share.canNativeShare()) {
      await this.share.nativeShare();
    } else {
      await this.onCopyLink();
    }
  }

  close(): void {
    this.personalNote = '';
    this.copied = false;
    this.shareWithAccount = false;
    this.share.close();
  }
}